/**
 * Login-Callback des Kundenportals (KEIN Login noetig zum Aufrufen) - Fundament P0
 * (docs/KONZEPT-KUNDENKONTO.md). Der Link aus der Magic-Link-Mail (pages/kundenportal.tsx) zeigt
 * hierher: `?token=...` aus der URL. NOCH KEINE echte Dashboard-Ansicht - die kommt in P1
 * (Vorgangs-/Rechnungsuebersicht, separater Auftrag nach Freigabe-Checkpoint).
 *
 * Der eigentliche Verbrauch (POST /api/public/customer-portal/consume-link) laeuft NICHT
 * automatisch beim blossen Seitenaufruf, sondern erst nach einem expliziten Klick auf "Jetzt
 * einloggen" (S9-Fix, Security-Review vor dem P0-Merge): Mail-Link-Prescan-Bots (Sicherheits-Gateways,
 * die Links in eingehenden Mails vorab abrufen, um sie auf Schadsoftware zu pruefen) wuerden sonst
 * den Einmal-Token verbrennen, bevor der Kunde selbst klickt - Standardempfehlung fuer Magic-Link-
 * Flows. Ein automatischer Prescan-Abruf dieser Seite loest dadurch keinen POST aus.
 *
 * Der zurueckgegebene Session-Token wird in localStorage abgelegt (Muster-Entscheidung: dieses
 * Repo nutzt an KEINER Stelle Cookies, auch das Mitarbeiter-Login nicht - Bearer-Token statt
 * Cookie, s. lib/auth.ts#extractBearer). P1+ liest denselben Schluessel wieder aus
 * (lib/customer-portal-client.ts, seit P1 die gemeinsame Quelle statt hier lokal definiert).
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Icon, Button, ButtonLink } from "../ui/components";
import { CUSTOMER_SESSION_STORAGE_KEY, setCustomerSessionToken } from "../lib/customer-portal-client";
import { GastRahmen } from "../ui/BetriebsKopf";

type Phase = "ready" | "consuming" | "done" | "error";

export { CUSTOMER_SESSION_STORAGE_KEY };

export default function KundenportalAnmeldenPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [phase, setPhase] = useState<Phase>(token ? "ready" : "error");
  const [error, setError] = useState<string | null>(
    token ? null : "Dieser Link ist unvollständig. Bitte fordern Sie einen neuen Login-Link an.",
  );
  const [email, setEmail] = useState<string | null>(null);

  async function login() {
    if (!token) return;
    setPhase("consuming");
    setError(null);
    try {
      const res = await fetch("/api/public/customer-portal/consume-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? "Dieser Link ist ungültig oder bereits abgelaufen.");
      }
      const body = (await res.json()) as { data: { sessionToken: string; email: string } };
      // localStorage kann in seltenen Browser-Einstellungen scheitern (privates Fenster mit
      // blockiertem Site-Data o.ae.) - setCustomerSessionToken faengt das intern ab (P0-Muster):
      // die Session bleibt fuer DIESEN Aufruf trotzdem gueltig, nur ein Neuladen faende sie nicht
      // wieder. Kein Blocker.
      setCustomerSessionToken(body.data.sessionToken);
      setEmail(body.data.email);
      setPhase("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("error");
    }
  }

  return (
    <GastRahmen>
      <div className="text-center">
      {phase === "ready" && (
        <>
          <Icon name="lock_open" className="mx-auto mb-3 text-[40px] text-secondary dark:text-secondary-fixed-dim" />
          <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">Login-Link gefunden</h1>
          <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Klicken Sie auf "Jetzt einloggen", um sich im Kundenportal anzumelden.
          </p>
          <Button type="button" className="mt-6 w-full !py-4" onClick={() => void login()}>
            Jetzt einloggen
          </Button>
        </>
      )}
      {phase === "consuming" && <p className="font-body-sm text-body-sm text-secondary">Wird eingeloggt …</p>}
      {phase === "error" && (
        <>
          <Icon name="error" className="mx-auto mb-3 text-[40px] text-error" />
          <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">Anmeldung fehlgeschlagen</h1>
          <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{error}</p>
          <ButtonLink href="/kundenportal" className="mt-6 inline-block !py-3 !px-6">
            Neuen Login-Link anfordern
          </ButtonLink>
        </>
      )}
      {phase === "done" && (
        <>
          <Icon name="check_circle" className="mx-auto mb-3 text-[40px] text-tertiary dark:text-tertiary-fixed-dim" />
          <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">Sie sind eingeloggt</h1>
          <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {email ? `Angemeldet als ${email}.` : "Anmeldung erfolgreich."}
          </p>
          {/* Kundenkonto P1: von hier direkt ins Dashboard (Vorgangsuebersicht) statt einer
              Sackgasse - kundenportal.tsx erkennt die soeben abgelegte Session selbst. */}
          <ButtonLink href="/kundenportal" className="mt-6 inline-block !py-4 !px-8">
            Zu meinen Vorgängen
          </ButtonLink>
        </>
      )}
      </div>
    </GastRahmen>
  );
}
